import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import { Box, Button, IconButton, Modal, Stack, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import loader from 'src/components/LoaderImg/loader.gif';
import { dsnDeletePermissionTreatment } from 'src/config';
import useCurrentUser from 'src/hooks/use-current-user';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import {
  deleteDailySessionNoteAction,
  resetEsignAction,
  submitEsignAction,
} from 'src/redux/actions/dailySessionNotes';
import {
  addSignOnBehalfAction,
  getTherapistInfoAction,
  resetNewDocumentSignAction,
  resetSignOnBehalfAction,
  resetSubmissionEventAction,
  undoDocumentSignAction,
} from 'src/redux/actions/documentationAction';
import SignOnBehalfModal from '../document-management/signature-on-behalf-modal';
import { getAccessToken } from 'src/routes/AuthContext';
import { modalStyle } from 'src/utils/common-properties';
import { BootstrapDialog } from '../common-modals/BootstrapDialog';
import SelectSuperVisor from '../document-management/select-supervisor';
import SignDocementModel from '../document-management/signature-document-popup';
import { Seo } from '../seo';
import SucessModal from '../sucess-modal';
import UndoSignConformation from './undo-Sign-conformation';
import { getDSNNoteSummary, resetDsnSummary } from 'src/redux/actions/therapist-dashboard-action';
import DSNSummaryModal from './DSN summary/DSNSummaryModal';
import DeleteNoteConfirmation from './delete-note-confirmation';
import { isDeletableDsnStatus } from './dsn-delete-eligibility';
import moment from 'moment';
// THIN-7916: facility-local today for the future-date edit guard.
import { getFacilityTodayYmd } from 'src/utils/convertDateToSelectedTimeZone';
import { TrashIcon } from '../therapyPlanning_2.0/commonComponent/therapy-planning-icons';
import ArchiveOutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PauseIcon from '@mui/icons-material/Pause';
import CheckIcon from '@mui/icons-material/Check';
import {
  ArchivedBadge,
  archivedTitleSx,
  useArchiveNote,
} from '../document-management/archive-note';
import { HeldFromBillingBadge, useHoldFromBilling } from '../document-management/hold-from-billing';
import { toNoteDocument } from './daily-note-document';
import { dsnPageTestId } from './test-ids';

type Props = {
  dailySessionNotesInfoResponse?: any;
  selectedPatient?: any;
  setOpen: any;
  selectedDate: any;
  setOpenEditNotes: any;
  isInClosedPeriod?: boolean;
  onClosedPeriodAction?: () => void;
  //  BRK-2086: the page's "Acting on behalf of" pick (BRK-2075). Non-null ONLY in full
  //  compliance mode — the parent derives it behind the epic Split + scope and clears it on a
  //  mode drop, so every fork below inherits that gating. Null/absent ⇒ byte-identical legacy.
  onBehalfTarget?: any;
};

function ViewSOAPDocument({
  dailySessionNotesInfoResponse,
  selectedPatient,
  setOpen,
  selectedDate,
  setOpenEditNotes,
  isInClosedPeriod = false,
  onClosedPeriodAction,
  onBehalfTarget = null,
}: Props) {
  const dispatch = useDispatch();
  const providerId = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.providerId
  );
  const dailySessionNotesInfo = useSelector((state: any) => state?.getDailySessionNotes?.response);
  const [discipline, firstName, lastName] = useCurrentUser(['discipline', 'firstName', 'lastName']);
  //  THIN-7799: the delete affordance needs the treatment ON.
  //  Fails closed — the hook returns null until the Split SDK is ready.
  const [isDsnDeleteEnabled] = useFeatureToggleWhenReady(
    dsnDeletePermissionTreatment,
    'ENTERPRISE'
  );
  //  THIN-7806: a note dated inside a CLOSED month-end period is not deletable — the only
  //  eligibility blocker per the THIN-7634 grooming decision. The EoC list already hides
  //  Delete for these via the BE `deletable` flag (THIN-7789/THIN-7636); this page never
  //  checked, so it offered a closed-period bypass the BE then rejected. Folded into
  //  canDelete rather than the JSX so every delete affordance on this page inherits it.
  //  BOPS-446: no permission check — the button also requires the note's BE `deletable` flag,
  //  which is the server's answer for this user (Any key, or Own key on a note they wrote), so
  //  an own-only user sees Delete on their own notes and not on anyone else's.
  const canDelete = Boolean(isDsnDeleteEnabled) && !isInClosedPeriod;
  // Facility name for the delete-confirmation context line: match the active
  // facilityId against the assigned-facilities dropdown (same source the clock-in
  // and report screens use — there's no facility name on the profile).
  const getAssignedFacilities = useSelector((state: any) => state.getFacilityDropDownReducer);
  const facilityName =
    getAssignedFacilities?.response?.data?.data?.find(
      (fac: any) => fac?.uuid === localStorage.getItem('facilityId')
    )?.name ?? '';
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const deleteDsnState = useSelector((state: any) => state?.deleteDailySessionNoteReducer);
  // DSN uuid = dailySessionNoteUuid on the document (not the form-submission uuid).
  const dsnUuid = selectedPatient?.name
    ? dailySessionNotesInfo?.dailySessionNote?.uuid
    : dailySessionNotesInfoResponse?.dailySessionNote?.uuid;

  const [esignConfirmModal, setEsignConfirmModal] = useState<{
    success: boolean;
    warning: boolean;
    //  BRK-2086: set only by the proxy-sign failure branch; legacy setters leave it undefined
    //  and the modal falls back to the esign response message exactly as before.
    message?: string;
  }>({
    success: false,
    warning: false,
  });
  const submitEsignResponse = useSelector((state: any) => state.submitEsignReducer);

  const [undoSign, setUndoSign] = useState(false);
  const [iframeRef, setIframeRef] = useState<any | null>(null);
  const [openNewSignModel, setOpenNewSignModel] = useState(false);
  const [isDocSigned, setIsDocSigned] = useState({
    isSuccessful: false,
    pdfUrl: '',
  });
  const dsnSummary = useSelector((state: any) => state.getDsnSummaryReducer);
  const [openSummary, setOpenSummary] = useState(false);

  const updateIFrameRef = useCallback((node: any) => {
    setIframeRef(node);
  }, []);

  useEffect(() => {
    const iframeElement = iframeRef;

    if (iframeElement) {
      const interval = setInterval(async () => {
        const response = await getAccessToken();
      }, 15000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [iframeRef]);

  const selectedPatientName = `${selectedPatient?.firstName} ${selectedPatient?.lastName}`;
  const callGetNoteTemplate = () => {
    if (isInClosedPeriod) {
      onClosedPeriodAction?.();
      return;
    }
    if (
      (selectedPatient?.name
        ? dailySessionNotesInfo?.dailySessionNote?.status
        : dailySessionNotesInfoResponse?.status) === 'SIGNED'
    ) {
      setUndoSign(true);
      return;
    }
    setOpenEditNotes(true);
  };

  const submitForEsign = () => {
    dispatch(resetNewDocumentSignAction());
    dispatch(
      submitEsignAction({
        uuid: selectedPatient?.name
          ? dailySessionNotesInfo?.dailySessionNote?.uuid
          : dailySessionNotesInfoResponse?.dailySessionNote?.uuid,
        formId: selectedPatient?.name
          ? dailySessionNotesInfo?.prefillKey?.split('/')[3]
          : dailySessionNotesInfoResponse?.prefillKey?.split('/')[3],
        //  BRK-2086: in on-behalf mode the prep is target-keyed. therapistId becomes the
        //  document author in the endpoint's no-form-submission fallback, and the caller's
        //  discipline (NOT_APPLICABLE for the compliance persona) is meaningless for the note —
        //  the note belongs to the target.
        therapistId: onBehalfTarget?.uuid ?? providerId,
        type: onBehalfTarget?.discipline ?? discipline,
      })
    );
  };

  const closeConfirmationModal = () => {
    setEsignConfirmModal({
      success: false,
      warning: false,
    });
    dispatch(resetEsignAction());
  };

  const checkAddSupervisorResponse = useSelector((state: any) => state.callSubmitEventReducer);
  const [triggerSelectSupervisor, setTriggerSupervisor] = useState(false);
  const closeSupervisorModal = () => {
    dispatch(resetEsignAction());
    setTriggerSupervisor(false);
  };

  useEffect(() => {
    if (!submitEsignResponse.isSubmitted && submitEsignResponse.response === null) return;
    if (!submitEsignResponse.isSubmitted && submitEsignResponse.response !== null) {
      if (
        submitEsignResponse.response?.message &&
        submitEsignResponse.response?.message?.includes('SUPERVISOR_REQUIRED')
      ) {
        setTriggerSupervisor(true);
        return;
      } else return;
    }
    if (!submitEsignResponse.isSubmitted && submitEsignResponse.response !== null) {
      setEsignConfirmModal({
        success: false,
        warning: true,
      });
    }
    if (submitEsignResponse.isSubmitted && submitEsignResponse.response !== null) {
      //  BRK-2086: in on-behalf mode the prep success opens the proxy modal instead of the
      //  direct PIN dialog — the direct path would mint the CALLER's identity and the backend
      //  hard-fails a blank license (the compliance persona holds none, by design).
      if (onBehalfTarget?.uuid) {
        dispatch(resetSignOnBehalfAction());
        //  The on-behalf card in the modal renders from this fetch, same as the EoC lane.
        dispatch(getTherapistInfoAction({ providerId: onBehalfTarget.uuid }));
        setOpenSignOnBehalfModal(true);
      } else {
        setOpenNewSignModel(true);
      }
      dispatch(resetEsignAction());
    }
  }, [submitEsignResponse]);

  //  BRK-2086: the compliance proxy sign for the DSN, mirroring the signature-preview lane
  //  (signature-template-new) — therapist axis, the note's own form submission, the caller's
  //  id as dorUuid (the backend verifies it against the JWT and resolves the on-behalf-of
  //  identity from the document author). Routed through the BRK-2057 PROXY_FOR_THERAPIST
  //  branch, which carries the license exemption and the on-behalf attribution.
  const signOnBehalf = useSelector((state: any) => state.addSignOnBehalfReducer);
  const [openSignOnBehalfModal, setOpenSignOnBehalfModal] = useState(false);

  const handleSignOnBehalfSubmit = (
    _document: any,
    signature: string,
    reason: string,
    consentConfirmation: boolean,
    pin?: string
  ) => {
    dispatch(
      addSignOnBehalfAction({
        formSubmissionUuid: dailySessionNotesInfoResponse?.response?.formSubmissionId,
        signerName: signature,
        consentConfirmation,
        isSupervisor: false,
        documentType: 'DAILY_SOAP_NOTE',
        dorUuid: providerId,
        isProxySignature: true,
        proxySignReason: reason,
        //  THIN-7775: the service layer moves this into the X-Signing-Pin header.
        ...(pin ? { pin } : {}),
      })
    );
  };

  useEffect(() => {
    //  Only react while OUR modal is in flight. The slice is shared (legacy DOR proxy + the EoC
    //  preview lane, which resets it on unmount) — without this gate, a stale response seeded
    //  before mount would toast "Failed E-Sign" at a user who never touched sign-on-behalf.
    if (!openSignOnBehalfModal) return;
    if (signOnBehalf?.response === null && !signOnBehalf?.isSignOnBehalf) return;
    if (signOnBehalf?.response !== null && !signOnBehalf?.isSignOnBehalf) {
      //  Structured PIN challenges (wrong pin / lockout / no pin set) are handled inline by the
      //  modal itself — closing here would throw the retry away (THIN-7706 convention).
      const outcome = signOnBehalf?.response?.data?.outcome;
      if (outcome === 'LOCKED' || outcome === 'WRONG_PIN' || outcome === 'NO_PIN_SET') {
        return;
      }
      setOpenSignOnBehalfModal(false);
      setEsignConfirmModal({
        success: false,
        warning: true,
        message: signOnBehalf?.response?.message,
      });
      return;
    }
    //  Success: close the modal; the list refetch (and with it the SIGNED status on this pane)
    //  rides the addSignOnBehalfReducer watcher in patientListing, mirroring the direct path's
    //  newDocumentSignReducer contract.
    setOpenSignOnBehalfModal(false);
  }, [signOnBehalf, openSignOnBehalfModal]);

  //  BRK-2086 (mode-drop hardening, BRK-2075 pattern): a facility switch flaps the mode with no
  //  remount — the parent clears the pick, and an open proxy modal must not survive it, or a
  //  stale target could be signed for.
  useEffect(() => {
    if (!onBehalfTarget && openSignOnBehalfModal) {
      setOpenSignOnBehalfModal(false);
    }
  }, [onBehalfTarget, openSignOnBehalfModal]);

  useEffect(() => {
    if (!checkAddSupervisorResponse.isEventCalled && checkAddSupervisorResponse.response === null)
      return;
    dispatch(resetSubmissionEventAction());
    submitForEsign();
  }, [checkAddSupervisorResponse]);

  const undoDocumentSignResponse = useSelector((state: any) => state.undoDocumentSignReducer);

  useEffect(() => {
    if (undoDocumentSignResponse?.isSuccess) {
      setUndoSign(false);
    }
  }, [undoDocumentSignResponse]);

  const confirmUndoSing = () => {
    // THIN-7258: always route through the FSM-aware unsign endpoint. The
    // legacy /provider/document/edit-signed-document is a deprecated
    // no-op (DocumentationServiceImpl.editSignedDocument). The unified
    // FSM (THIN-7164) handles both reform-signed and legacy Jotform-
    // signed docs.
    dispatch(
      undoDocumentSignAction({
        formSubmissionUuid: dailySessionNotesInfoResponse?.response?.formSubmissionId,
        documentType: 'DAILY_SOAP_NOTE',
      })
    );
  };

  const handleCloseNewSignModel = () => {
    setOpenNewSignModel(false);
  };

  const handleOpenDsnSummary = (patient: any) => {
    dispatch(resetDsnSummary());
    dispatch(
      getDSNNoteSummary({
        patientId: patient?.uuid,
        type: 'dashboard',
        params: {
          discipline: discipline,
          localDate: moment(selectedDate).format('YYYY-MM-DD'),
        },
      })
    );
    setOpenSummary(true);
  };

  const handleDeleteNote = () => {
    //  THIN-7802: a closed month is the only eligibility blocker (THIN-7634 grooming), and
    //  the BE rejects the delete outright. Route to the same explanation dialog Edit uses
    //  rather than silently doing nothing.
    if (isInClosedPeriod) {
      onClosedPeriodAction?.();
      return;
    }
    setOpenDeleteModal(true);
  };

  //  BOPS-175 — the same two actions the Episode of Care carries, on the page the DOR actually
  //  works on. Same hooks, same dialogs, same gates: each hook already folds in its own feature
  //  toggle, permission, closed-month rule and (for Archive) the has-a-PDF rule, so nothing about
  //  when they may be used is decided here.
  const holdFromBilling = useHoldFromBilling();

  /**
   * What just happened to this note on this screen.
   *
   * The Episode of Care keeps its badges honest by patching the row in its documents list —
   * `applyHeldFromBilling` and `applyArchived` both do exactly that. This page has no such list:
   * the note comes from `/provider/v2/daily-notes/all`, which carries neither flag and which
   * nothing patches. Without this the action succeeds and the bar never changes.
   *
   * Cleared whenever a different note is opened, so one note's result can't bleed onto another.
   */
  const [recentAction, setRecentAction] = useState<{
    heldFromBilling?: boolean;
    holdOrigin?: 'MANUAL' | 'SYSTEM' | null;
    archived?: boolean;
  }>({});

  useEffect(() => {
    setRecentAction({});
  }, [dsnUuid]);

  const archiveNote = useArchiveNote({
    patientName: selectedPatientName?.trim(),
    //  The hook resets its own success flag as soon as it fires, so this is the only chance to
    //  observe it — there is nothing left in the store to read afterwards.
    onArchived: () => setRecentAction((prev) => ({ ...prev, archived: true })),
  });

  /**
   * Archive and Hold in a closed month: OFFERED, then refused with the closed-period dialog.
   *
   * The hooks' own `canArchive` / `canHold` / `canInclude` fold the closed-period rule in and so
   * make the control vanish, which is right in the Episode of Care row menus and wrong here —
   * this page is one note, and a control that is simply absent reads as a bug or a missing
   * permission with nothing to click to find out why. So the button renders on
   * `can… || isBlockedByClosedPeriod` and the refusal moves into the click, exactly as Edit and
   * Start Note on this same page already answer a closed period.
   *
   * The hooks stay the authority: each still refuses the action internally, so this routing only
   * decides which of the two things a click does, never whether the action may run. Only this page
   * calls the `…BlockedByClosedPeriod` predicates — the Episode of Care lists are untouched.
   */
  const requestArchive = (document: any) => {
    archiveNote.requestArchive(document);
  };

  const requestHold = (document: any) => {
    if (holdFromBilling.isHoldBlockedByClosedPeriod(document)) {
      onClosedPeriodAction?.();
      return;
    }
    holdFromBilling.requestHold(document);
  };

  const includeInBilling = (document: any) => {
    if (holdFromBilling.isIncludeBlockedByClosedPeriod(document)) {
      onClosedPeriodAction?.();
      return;
    }
    holdFromBilling.includeInBilling(document);
  };

  const holdState = useSelector((state: any) => state?.holdDailyNoteFromBillingReducer);
  useEffect(() => {
    //  Both directions: holding sets the badge, including in billing takes it away again.
    if (holdState?.isSuccess && holdState?.dailySessionNoteUuid === dsnUuid) {
      // THIN-8050: a hold placed here is a person's, so the badge shows the manual variant.
      setRecentAction((prev) => ({
        ...prev,
        heldFromBilling: holdState?.hold === true,
        holdOrigin: holdState?.hold === true ? 'MANUAL' : null,
      }));
    }
  }, [holdState?.isSuccess, holdState?.dailySessionNoteUuid, holdState?.hold, dsnUuid]);

  const noteDocument = {
    ...toNoteDocument({
      dsnUuid,
      selectedPatient,
      selectedDate,
      pdfUrl: dailySessionNotesInfoResponse?.response?.pdfUrl,
      therapistName: `${firstName || ''} ${lastName || ''}`.trim(),
    }),
    //  Last, so what the user just did wins over a row that predates it.
    ...recentAction,
  };

  const isArchived = archiveNote.isArchived(noteDocument);
  const isHeld = holdFromBilling.isHeldFromBilling(noteDocument);
  //  An archived note is read-only from here on: the underlying delete has already run, so Delete,
  //  Archive, Hold and Edit would all fail for anyone. Therapy Info and DSN Summary stay.
  const isReadOnly = archiveNote.isReadOnly(noteDocument);

  const handleConfirmDeleteNote = (reason: string) => {
    // reason is required + non-blank (BE 400s otherwise); the modal already enforces this.
    dispatch(deleteDailySessionNoteAction({ dsnUuid, reason }));
  };

  useEffect(() => {
    if (deleteDsnState?.isSuccess) {
      setOpenDeleteModal(false);
      setOpenEditNotes(false);
    }
  }, [deleteDsnState?.isSuccess]);
  return (
    <>
      <Seo title="Patient: Session Notes" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
        }}
      >
        <Stack
          justifyContent="space-between"
          flexDirection="row"
          alignItems="center"
        >
          {selectedPatient ? (
            <Stack
              flexDirection="row"
              alignItems="center"
              gap="0.5rem"
            >
              <Typography
                sx={{ fontSize: '18px !important', ...(isArchived ? archivedTitleSx : {}) }}
                variant="h6"
              >
                {selectedPatientName
                  ? selectedPatientName?.endsWith("'s")
                    ? selectedPatientName
                    : `${selectedPatientName}'s`
                  : ''}{' '}
                notes
              </Typography>
              {/*  Archived wins: a note can be both, but archiving is the one that took it out
                   of billing for good, and two badges side by side read as two states. */}
              {isArchived ? (
                <ArchivedBadge />
              ) : isHeld ? (
                <HeldFromBillingBadge origin={holdFromBilling.holdOrigin(noteDocument)} />
              ) : null}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: '18px !important' }}
              variant="h6"
            >
              No Records Found
            </Typography>
          )}

          <Stack
            flexDirection="row"
            gap="1rem"
            sx={{ height: 40 }}
          >
            {canDelete &&
              !isReadOnly &&
              isDeletableDsnStatus(selectedPatient?.status) &&
              selectedPatient?.deletable && (
                <Tooltip title="Delete Note">
                  <IconButton
                    color="error"
                    onClick={handleDeleteNote}
                    data-testid={dsnPageTestId('view-delete-note')}
                    sx={{
                      borderRadius: '11.765px',
                      background: 'var(--error-light, #FEE4E2)',
                    }}
                  >
                    <TrashIcon />
                  </IconButton>
                </Tooltip>
              )}
            {/*  BOPS-175 mock 1b — Archive sits immediately beside Delete: the two irreversible
                 actions together, where the trash already is. Neutral rather than a second red
                 button, per the mock — Delete stays the only destructive-coloured control, since
                 two red buttons side by side read as one control with a stutter. */}
            {archiveNote.canArchive(noteDocument) && (
              <Tooltip title="Archive Note">
                <IconButton
                  onClick={() => requestArchive(noteDocument)}
                  data-testid={dsnPageTestId('archive-note')}
                  sx={{
                    borderRadius: '11.765px',
                    border: '1px solid #E5E7EB',
                    color: '#6C737F',
                    '&:hover': { borderColor: '#6366F1', background: '#EBEEFE' },
                  }}
                >
                  <ArchiveOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {/*  Hold from billing is the frequent everyday one, so it is a labelled button rather
                 than an icon — it has to read at a glance. On a held note it flips to Include in
                 billing; only one of the two is ever offered, because the hook's own predicates
                 are mutually exclusive on the note's state. */}
            {(holdFromBilling.canHold(noteDocument) ||
              holdFromBilling.isHoldBlockedByClosedPeriod(noteDocument)) && (
              <Button
                color="primary"
                variant="outlined"
                onClick={() => requestHold(noteDocument)}
                data-testid={dsnPageTestId('hold-from-billing')}
                startIcon={<PauseIcon />}
                sx={{ whiteSpace: 'nowrap', overflow: 'hidden' }}
              >
                Hold from billing
              </Button>
            )}
            {(holdFromBilling.canInclude(noteDocument) ||
              holdFromBilling.isIncludeBlockedByClosedPeriod(noteDocument)) && (
              <Button
                color="primary"
                variant="outlined"
                onClick={() => includeInBilling(noteDocument)}
                data-testid={dsnPageTestId('include-in-billing')}
                startIcon={<CheckIcon />}
                sx={{ whiteSpace: 'nowrap', overflow: 'hidden' }}
              >
                Include in billing
              </Button>
            )}
            {!isReadOnly ? (
              <Button
                color="primary"
                disabled={moment(selectedDate).format('YYYY-MM-DD') > getFacilityTodayYmd()}
                variant="contained"
                onClick={callGetNoteTemplate}
                data-testid={dsnPageTestId('edit-note')}
              >
                Edit
              </Button>
            ) : null}

            <Button
              color="primary"
              variant="outlined"
              startIcon={<ArticleOutlinedIcon />}
              onClick={() => setOpen((prev: any) => !prev)}
              data-testid={dsnPageTestId('view-therapy-info')}
              sx={{
                whiteSpace: 'nowrap',
                // width: 145,
                overflow: 'hidden',
              }}
            >
              Therapy Info
            </Button>
            <Button
              color="primary"
              variant="outlined"
              sx={{
                whiteSpace: 'nowrap',
                // width: 145,
                overflow: 'hidden',
              }}
              onClick={() => handleOpenDsnSummary(selectedPatient)}
              data-testid={dsnPageTestId('view-dsn-summary')}
            >
              DSN Summary
            </Button>
            {!isReadOnly &&
            (selectedPatient?.name
              ? dailySessionNotesInfo?.dailySessionNote?.status
              : dailySessionNotesInfoResponse?.status) === 'FINALIZED' ? (
              <Button
                color="primary"
                variant="contained"
                startIcon={
                  submitEsignResponse.loading ? (
                    <img
                      src={loader}
                      style={{ height: 30 }}
                      alt="Daily soap note"
                    />
                  ) : (
                    <TaskOutlinedIcon />
                  )
                }
                onClick={submitForEsign}
                data-testid={dsnPageTestId('sign-document')}
                sx={{
                  whiteSpace: 'nowrap',
                  width: 145,
                  overflow: 'hidden',
                }}
                disabled={submitEsignResponse.loading}
              >
                Sign Document
              </Button>
            ) : null}
          </Stack>
        </Stack>

        {dailySessionNotesInfoResponse?.loading ? (
          <img
            src={loader}
            alt="loading"
            style={{ margin: 'auto', display: 'block', marginTop: '10%' }}
          />
        ) : (
          <Box
            sx={{ height: '100%' }}
            py={3}
          >
            <iframe
              src={dailySessionNotesInfoResponse?.response?.pdfUrl}
              width={'100%'}
              ref={updateIFrameRef}
              height={'100%'}
              style={{ border: 'none' }}
              title="daily session note"
            />
          </Box>
        )}
      </Box>

      <Modal open={esignConfirmModal.warning}>
        <SucessModal
          close={closeConfirmationModal}
          isSucess={esignConfirmModal.success}
          title={' Failed E-Sign'}
          message={
            esignConfirmModal.success
              ? 'e-Sign is submitted successfully for this SOAP note'
              : esignConfirmModal.message ?? submitEsignResponse.response?.message
          }
        />
      </Modal>

      {/* BRK-2086: the compliance proxy sign for a DSN in on-behalf mode — same modal and
          contract as the signature-preview lane; therapist axis only on this page. */}
      <BootstrapDialog
        onClose={() => setOpenSignOnBehalfModal(false)}
        aria-labelledby="sign-on-behalf-dialog-title"
        open={openSignOnBehalfModal}
      >
        <SignOnBehalfModal
          onClose={() => setOpenSignOnBehalfModal(false)}
          onSign={handleSignOnBehalfSubmit}
          //  Echoed back through onSign and otherwise unread — the submit handler addresses the
          //  note by its form-submission uuid directly.
          signOnBehalfDoc={{
            uuid: dailySessionNotesInfoResponse?.response?.formSubmissionId,
            formType: 'DAILY_SOAP_NOTE',
          }}
          onBehalfOfLabel="Therapist"
          signerLabel="Signer"
        />
      </BootstrapDialog>
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '750px' } }}
        onClose={() => setUndoSign(false)}
        aria-labelledby="customized-dialog-title"
        open={undoSign}
      >
        <UndoSignConformation
          close={() => setUndoSign(false)}
          confirm={confirmUndoSing}
        />
      </BootstrapDialog>

      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '520px' } }}
        onClose={() => setOpenDeleteModal(false)}
        aria-labelledby="delete-note-dialog-title"
        open={openDeleteModal}
      >
        <DeleteNoteConfirmation
          close={() => setOpenDeleteModal(false)}
          confirm={handleConfirmDeleteNote}
          patientName={selectedPatientName?.trim()}
          dateOfService={selectedDate ? moment(selectedDate).format('MMMM D, YYYY') : ''}
          therapistName={`${firstName || ''} ${lastName || ''}`.trim()}
          facility={facilityName}
          loading={deleteDsnState?.loading}
        />
      </BootstrapDialog>

      {/*  BOPS-175 — the Episode of Care's dialogs verbatim, not a second variant. Each hook
           renders null when its own gate is closed, so neither needs a guard here. */}
      {holdFromBilling.holdConfirmationDialog}
      {archiveNote.archiveConfirmationDialog}

      {/* For selecting a supervisor -> dummy data*/}
      <Modal
        style={modalStyle}
        open={triggerSelectSupervisor}
        onClose={closeSupervisorModal}
      >
        <div>
          <SelectSuperVisor
            activeTab={
              selectedPatient?.discipline || (typeof discipline === 'string' ? discipline : '')
            }
            close={closeSupervisorModal}
            selectedDocument={dailySessionNotesInfoResponse?.dailySessionNote}
            role={''}
            source={'FROM_DSN'}
            DSNId={submitEsignResponse.response?.message?.split(':')[1]?.trim()}
          />
        </div>
      </Modal>

      <BootstrapDialog
        PaperProps={{ style: { width: '600px' } }}
        onClose={handleCloseNewSignModel}
        aria-labelledby="customized-dialog-title"
        open={openNewSignModel}
      >
        <SignDocementModel
          onClose={handleCloseNewSignModel}
          setIsDocSigned={setIsDocSigned}
          formSubmissionUuid={dailySessionNotesInfoResponse?.response?.formSubmissionId}
          isSupervisor={false}
          docType={'DAILY_SOAP_NOTE'}
          open
        />
      </BootstrapDialog>
      <DSNSummaryModal
        open={openSummary}
        title={`DSN Summary | ${discipline}`}
        onClose={() => setOpenSummary(false)}
        data={dsnSummary?.response}
      />
    </>
  );
}

export default ViewSOAPDocument;
